import { canChooseLandingPage, getLandingPageOptions } from "../utils/navigation";
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";
import practiceInstructorService from "../services/practiceInstructorService";
import { cache } from "../utils/cacheUtils";
import { formatRoleName } from "../utils/stringUtils";
import PasswordManagementModal from "./auth/PasswordManagementModal";

function Profile() {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingProfession, setIsEditingProfession] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [assignedInstructor, setAssignedInstructor] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  React.useEffect(() => {
    const fetchInstructor = async () => {
      if (user && user.roles && user.roles.some((r) => r.name === "LERNENDE")) {
        try {
          const instructor =
            await practiceInstructorService.getCurrentInstructor(user.id);
          setAssignedInstructor(instructor);
        } catch (e) {
          console.warn("Failed to fetch assigned instructor", e);
        }
      }
    };
    fetchInstructor();
  }, [user]);

  const landingOptions = getLandingPageOptions(user);
  const handleLandingPageChange = async (event) => {
    const page = event.target.value;
    setLoading(true);
    setError(null);
    setUpdateMessage("");
    try {
      const result = await authService.updateLoginPreferences(page);
      if (!result.success) {
        setError(result.message);
        return;
      }
      const refreshed = await refreshProfile();
      if (!refreshed?.success) {
        setError("Preference saved, but the profile could not be refreshed. Please reload the page.");
        return;
      }
      setUpdateMessage("Login preference saved successfully!");
    } catch (err) {
      setError("Login preference could not be saved");
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfession = () => {
    // Prevent learners from editing their profession type
    if (user?.roles?.some((r) => r.name === "LERNENDE")) {
      setError("Apprentices cannot change their profession type");
      return;
    }
    setSelectedProfession(user.professionType || "");
    setIsEditingProfession(true);
    setError(null);
    setUpdateMessage("");
  };

  const handleCancelEditProfession = () => {
    setIsEditingProfession(false);
    setSelectedProfession("");
    setError(null);
  };

  const handleSaveProfession = async () => {
    if (!selectedProfession) {
      setError("Please select a profession type");
      return;
    }

    setLoading(true);
    setError(null);
    setUpdateMessage("");

    try {
      const result = await authService.updateUserProfession(
        user.id,
        selectedProfession,
      );
      if (result.success) {
        setUpdateMessage("Profession type updated successfully!");
        setIsEditingProfession(false);
        await refreshProfile();

        // Clear caches so lists are redownloaded with new profession permissions
        try {
          cache.clear("students-");
          cache.clear("classes-");
          cache.clear("schools-");
        } catch (e) {
          console.warn("Failed to clear cache", e);
        }

        setTimeout(() => setUpdateMessage(""), 3000);
      } else {
        setError(result.message || "Error updating profession type");
      }
    } catch (err) {
      setError("Error updating profession type");
    } finally {
      setLoading(false);
    }
  };

  const getProfessionTypeOptions = () => {
    const options = [
      { value: "INFORMATIK", label: "Informatik" },
      { value: "MEDIAMATIK", label: "Mediamatik" },
      { value: "KV", label: "KV" },
      { value: "KUNDENDIALOG", label: "Kundendialog" },
      { value: "DETAILHANDEL", label: "Detailhandel" },
    ];

    // Only admins can select ALL
    if (user?.roles?.some((r) => r.name === "ADMIN")) {
      options.push({ value: "ALL", label: "All Professions" });
    }

    return options;
  };

  const formatProfessionType = (professionType) => {
    switch (professionType) {
      case "ALL":
        return "All Professions";
      case "MEDIAMATIKER":
      case "MEDIAMATIK":
        return "Mediamatik";
      case "INFORMATIKER":
      case "INFORMATIK":
        return "Informatik";
      case "KV":
        return "KV";
      case "KUNDENDIALOG":
        return "Kundendialog";
      case "DETAILHANDEL":
        return "Detailhandel";
      default:
        return professionType || "Nicht zugewiesen";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    try {
      return new Date(dateString).toLocaleDateString("en-CH", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getRoleLabel = (roleName) => formatRoleName(roleName);

  if (!user) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="card-brand text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-4">
              Profile not available
            </h2>
            <p className="text-gray-600">Please log in again.</p>
          </div>
        </div>
      </div>
    );
  }

  // const { percent, completed, total } = getProfileCompletion();

  return (
    <div className="min-h-screen bg-green-cloud pb-20 sm:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-7 lg:py-8">
        {/* Header / Profile summary */}
        <div className="mb-5 sm:mb-7">
          <div className="card-brand-elevated p-4 sm:p-6 md:p-8 bg-gradient-to-r from-ice-white to-pale-dawn/40">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sunrise-red text-white flex items-center justify-center text-2xl md:text-3xl font-bold shadow-sm">
                    {user.firstName?.charAt(0) || user.username?.charAt(0)}
                  </div>
                  {user.professionType && (
                    <div
                      className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-sm shadow-sm ${
                        user.professionType === "INFORMATIKER" ||
                        user.professionType === "INFORMATIK"
                          ? "bg-blue-100 text-blue-600"
                          : user.professionType === "MEDIAMATIKER" ||
                              user.professionType === "MEDIAMATIK"
                            ? "bg-purple-100 text-purple-600"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <i
                        className={
                          user.professionType === "INFORMATIKER" ||
                          user.professionType === "INFORMATIK"
                            ? "fas fa-laptop-code"
                            : user.professionType === "MEDIAMATIKER" ||
                                user.professionType === "MEDIAMATIK"
                              ? "fas fa-palette"
                              : "fas fa-user"
                        }
                      ></i>
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left w-full">
                  <h1 className="text-2xl md:text-3xl font-bold text-grey-dusk flex items-center justify-center sm:justify-start gap-2 mb-1">
                    My Profile
                  </h1>
                  <p className="text-sm md:text-base text-slate-cloud mb-2">
                    View and manage your account information.
                  </p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-2">
                    {user.email && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-ice-white text-xs md:text-sm text-grey-dusk">
                        <i className="fas fa-envelope mr-2"></i>
                        {user.email}
                      </span>
                    )}

                    {user.professionType && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-sunrise-red/10 text-xs md:text-sm text-sunrise-red font-semibold">
                        <i className="fas fa-graduation-cap mr-2"></i>
                        {formatProfessionType(user.professionType)}
                      </span>
                    )}

                    {user.roles &&
                      user.roles.map((role) => (
                        <span
                          key={role.name}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-grey-dusk/10 text-[11px] md:text-xs text-grey-dusk font-medium"
                        >
                          <i className="fas fa-shield-alt mr-1.5 text-[10px]"></i>
                          {getRoleLabel(role.name)}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center text-sm">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}
        {updateMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center text-sm">
            <i className="fas fa-check-circle mr-2"></i>
            {updateMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:items-stretch">
          {/* Personal Information Card */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="card-brand overflow-hidden flex flex-col flex-1">
              <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white px-5 py-3 rounded-t-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fas fa-user text-base"></i>
                  <h2 className="text-base font-bold">Personal information</h2>
                </div>
                {loading && (
                  <span className="text-xs flex items-center gap-1 opacity-80">
                    <i className="fas fa-circle-notch fa-spin"></i> Saving…
                  </span>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                      Username
                    </label>
                    <div className="text-sm font-semibold text-grey-dusk">
                      {user.username}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                      Full name
                    </label>
                    <div className="text-sm font-semibold text-grey-dusk">
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                      Email
                    </label>
                    <div className="text-sm text-grey-dusk">
                      {user.email || "—"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                      Profession type
                    </label>
                    {!isEditingProfession ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-sunrise-red/10 text-sunrise-red">
                          <i className="fas fa-graduation-cap mr-1.5"></i>
                          {formatProfessionType(user.professionType)}
                        </span>
                        {!user?.roles?.some((r) => r.name === "LERNENDE") && (
                          <button
                            className="px-2 py-1 rounded text-[11px] bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium transition-colors flex items-center gap-1"
                            onClick={handleEditProfession}
                            disabled={loading}
                          >
                            <i className="fas fa-edit text-[10px]"></i> Edit
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <select
                          className="flex-1 input-brand text-sm py-1"
                          value={selectedProfession}
                          onChange={(e) =>
                            setSelectedProfession(e.target.value)
                          }
                          disabled={loading}
                        >
                          <option value="">Select…</option>
                          {getProfessionTypeOptions().map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          className="px-3 py-1.5 rounded bg-green-500 hover:bg-green-600 text-white text-xs font-medium transition-colors disabled:opacity-50"
                          onClick={handleSaveProfession}
                          disabled={loading || !selectedProfession}
                        >
                          {loading ? (
                            <i className="fas fa-circle-notch fa-spin"></i>
                          ) : (
                            <i className="fas fa-check"></i>
                          )}
                        </button>
                        <button
                          className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs transition-colors"
                          onClick={handleCancelEditProfession}
                          disabled={loading}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  {user.roles?.some((r) => r.name === "LERNENDE") && (
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">
                        Apprenticeship year
                      </label>
                      <div className="text-sm font-semibold text-grey-dusk">
                        {user.computedLehrjahr
                          ? `${user.computedLehrjahr}. Lehrjahr`
                          : "—"}
                      </div>
                    </div>
                  )}
                </div>

                {/* Roles */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="fas fa-shield-alt text-sunrise-red text-xs"></i>
                    <span className="text-xs font-semibold text-grey-dusk">
                      Roles & access
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {user.roles?.length > 0 ? (
                      user.roles.map((role) => (
                        <span
                          key={role.name}
                          className="inline-flex items-center px-2.5 py-1 rounded-full bg-ice-white border border-pale-dawn text-xs text-grey-dusk"
                        >
                          <i className="fas fa-user-tag mr-1.5 text-[10px]"></i>
                          {getRoleLabel(role.name)}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">
                        No roles assigned.
                      </span>
                    )}
                  </div>
                </div>

                {/* Assigned instructor (learners only) */}
                {assignedInstructor && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="fas fa-user-tie text-sunrise-red text-xs"></i>
                      <span className="text-xs font-semibold text-grey-dusk">
                        Assigned Trainer
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 p-2.5 rounded-lg">
                      <div className="w-8 h-8 bg-sunrise-red text-white flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0">
                        {assignedInstructor.firstName?.charAt(0) ||
                          assignedInstructor.username?.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-grey-dusk">
                          {assignedInstructor.firstName}{" "}
                          {assignedInstructor.lastName}
                        </div>
                        <div className="text-xs text-gray-400">
                          @{assignedInstructor.username}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {canChooseLandingPage(user) && (
              <section className="card-brand overflow-hidden mt-5">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                  <i className="fas fa-home text-sunrise-red" aria-hidden="true" />
                  <h2 className="text-base font-bold text-grey-dusk">Login preferences</h2>
                </div>
                <div className="p-4">
                  <label htmlFor="default-landing-page" className="label-brand">Default page after login</label>
                  <select
                    id="default-landing-page"
                    className="input-brand max-w-sm"
                    aria-describedby="default-landing-page-help"
                    value={landingOptions.some((item) => item.id === user.defaultLandingPage) ? user.defaultLandingPage : "DASHBOARD"}
                    disabled={loading || landingOptions.length === 0}
                    onChange={handleLandingPageChange}
                  >
                    {landingOptions.length === 0 && <option value="DASHBOARD">Dashboard</option>}
                    {landingOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                  </select>
                  <p id="default-landing-page-help" className="text-xs text-gray-500 mt-2">
                    Choose which page Journey should open automatically after you sign in.
                  </p>
                </div>
              </section>
            )}
          </div>

          {/* Right sidebar: Account info + Security */}
          <div className="lg:col-span-1 flex flex-col">
            {/* Account metadata */}
            <div className="card-brand overflow-hidden flex flex-col flex-1">
              <div className="bg-grey-dusk text-white px-5 py-3 rounded-t-xl flex items-center gap-2">
                <i className="fas fa-shield-alt text-base"></i>
                <h2 className="text-base font-bold">Account</h2>
              </div>
              <div className="p-4 space-y-3 flex flex-col flex-1">
                <div className="flex justify-between items-start text-xs">
                  <span className="text-gray-500">Created</span>
                  <span className="font-medium text-grey-dusk text-right ml-2">
                    {formatDate(user.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between items-start text-xs">
                  <span className="text-gray-500">Last updated</span>
                  <span className="font-medium text-grey-dusk text-right ml-2">
                    {formatDate(user.updatedAt)}
                  </span>
                </div>
                {user.lastLogin && (
                  <div className="flex justify-between items-start text-xs">
                    <span className="text-gray-500">Last login</span>
                    <span className="font-medium text-grey-dusk text-right ml-2">
                      {formatDate(user.lastLogin)}
                    </span>
                  </div>
                )}

                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">
                    Change your password to keep your account secure.
                  </p>
                  <button
                    onClick={() => setIsPasswordModalOpen(true)}
                    className="w-full border border-sunrise-red text-sunrise-red hover:bg-sunrise-red hover:text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <i className="fas fa-key text-xs"></i>
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PasswordManagementModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        user={user}
      />

      {loading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-40 pointer-events-none">
          <div className="bg-white rounded-xl shadow-lg px-5 py-3 flex items-center gap-3 text-sm text-grey-dusk">
            <i className="fas fa-circle-notch fa-spin text-sunrise-red"></i>
            Saving changes…
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
